import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { LOGIN } from "@/constants/testIds";
import { toast } from "sonner";
import { Phone, KeyRound, Sparkles } from "lucide-react";

export default function LoginPage() {
  const { requestOtp, verifyOtp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [existingUser, setExistingUser] = useState(false);
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [demoOtp, setDemoOtp] = useState("");

  // Lookup existing user's name as soon as phone is 10 digits
  React.useEffect(() => {
    if (phone.length !== 10) {
      setExistingUser(false);
      return;
    }
    let ignore = false;
    (async () => {
      try {
        const { data } = await api.get(`/auth/lookup`, { params: { phone } });
        if (ignore) return;
        if (data.exists && data.name) {
          setName(data.name);
          setExistingUser(true);
        } else {
          setExistingUser(false);
        }
      } catch { /* ignore */ }
    })();
    return () => { ignore = true; };
  }, [phone]);

  const sendOtp = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^\d{10}$/.test(phone)) {
      setError("Enter a 10-digit phone number");
      return;
    }
    setLoading(true);
    try {
      const res = await requestOtp(phone);
      setDemoOtp(res.demo_otp || "");
      setStep(2);
      toast.info(`Demo OTP: ${res.demo_otp || "123456"}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await verifyOtp(phone, otp, name);
      toast.success(`Welcome, ${u.name}`);
      if (u.role === "admin") navigate("/admin");
      else navigate(from, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid={LOGIN.page} className="min-h-screen grid place-items-center px-4 py-10">
      <div className="w-full max-w-sm bg-hyd-panel border border-hyd-border rounded-2xl p-6 shadow-card-dark">
        <div className="flex justify-between items-center mb-6">
          <Logo />
          <Link to="/" className="text-xs text-hyd-text2 hover:text-hyd-gold">Back</Link>
        </div>

        <h1 className="font-display text-3xl font-extrabold">
          {step === 1 ? "Sign in" : "Verify OTP"}
        </h1>
        <p className="text-hyd-text2 text-sm mt-1">
          {step === 1 ? "Enter your phone to receive a one-time password." : `Sent to +91 ${phone}`}
        </p>

        {step === 1 ? (
          <form onSubmit={sendOtp} className="mt-6 space-y-4">
            <div>
              <Label className="text-xs text-hyd-text2 mb-1 block flex items-center gap-1">
                <Phone className="w-3 h-3" /> Phone number
              </Label>
              <div className="flex">
                <span className="grid place-items-center px-3 rounded-l-md border border-r-0 border-hyd-border bg-hyd-bg text-hyd-text2 text-sm">
                  +91
                </span>
                <Input
                  data-testid={LOGIN.phoneInput}
                  inputMode="numeric"
                  placeholder="10-digit number"
                  value={phone}
                  maxLength={10}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  className="rounded-l-none bg-hyd-bg border-hyd-border text-hyd-text text-lg tracking-widest"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-hyd-text2 mb-1 block">
                Name {existingUser && <span className="text-hyd-gold text-[10px] uppercase tracking-widest">· welcome back</span>}
              </Label>
              <Input
                data-testid={LOGIN.nameInput}
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={`bg-hyd-bg text-hyd-text ${existingUser ? "border-hyd-gold shadow-glow-gold" : "border-hyd-border"}`}
              />
            </div>
            {error && <div className="text-hyd-red text-sm">{error}</div>}
            <Button
              data-testid={LOGIN.sendOtpBtn}
              type="submit"
              disabled={loading}
              className="w-full bg-hyd-gold text-hyd-bg hover:bg-hyd-goldDim font-bold uppercase tracking-wider"
            >
              {loading ? "Sending..." : "Send OTP"}
            </Button>
            <p className="text-[10px] text-hyd-text3 text-center">
              Admin? Use phone <span className="text-hyd-gold">9999999999</span>. Demo OTP <span className="text-hyd-gold">123456</span>.
            </p>
          </form>
        ) : (
          <form onSubmit={verify} className="mt-6 space-y-4">
            {demoOtp && (
              <div className="p-3 rounded-lg bg-hyd-gold/10 border border-hyd-gold/40 text-hyd-gold text-xs flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> Demo mode: use OTP <b>{demoOtp}</b>
              </div>
            )}
            <div>
              <Label className="text-xs text-hyd-text2 mb-1 block flex items-center gap-1">
                <KeyRound className="w-3 h-3" /> One-time password
              </Label>
              <Input
                data-testid={LOGIN.otpInput}
                inputMode="numeric"
                placeholder="6-digit OTP"
                value={otp}
                maxLength={6}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="bg-hyd-bg border-hyd-border text-hyd-text text-2xl tracking-[0.5em] text-center"
              />
            </div>
            {error && <div className="text-hyd-red text-sm">{error}</div>}
            <Button
              data-testid={LOGIN.verifyBtn}
              type="submit"
              disabled={loading}
              className="w-full bg-hyd-red hover:bg-hyd-redDim text-white font-bold uppercase tracking-wider"
            >
              {loading ? "Verifying..." : "Verify & continue"}
            </Button>
            <button type="button" onClick={() => setStep(1)} className="w-full text-xs text-hyd-text2 hover:text-hyd-gold">
              Change phone
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
